import { addHours, isAfter } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { calculateBusinessDayReminderTime, Holiday } from './businessDayCalculator';
// DTC-G G3: route the `calculateReminderTimes` default `now` parameter
// through the canonical clock so callers that omit `now` still get a
// freezable anchor under `clock.setFixed(...)`. Callers that supply
// `now` explicitly (e.g., enqueue-reminders edge functions) are
// unaffected.
import { clock } from './clock';
import {
  isInQuietHours as sharedIsInQuietHours,
  adjustForQuietHours as sharedAdjustForQuietHours,
} from '../../supabase/functions/_shared/quietHours';

export interface QuietHours {
  start: string; // HH:mm format
  end: string;   // HH:mm format
}

export interface ReminderCadence {
  immediate: boolean;
  hours24: boolean;
  hours2: boolean;
  businessDayMode?: boolean;
}

/**
 * Convert a time in shop timezone to UTC
 */
export function toUTC(date: Date, timezone: string): Date {
  return fromZonedTime(date, timezone);
}

/**
 * Convert a UTC time to shop timezone
 */
export function toShopTime(date: Date, timezone: string): Date {
  return toZonedTime(date, timezone);
}

/**
 * Quiet hours — delegated to the ONE authority the Edge dispatcher uses
 * (supabase/functions/_shared/quietHours.ts). Core-100 R4: this file used to carry its own copy,
 * which the tests exercised while enqueue-reminders ran a different (defective) one.
 */
export function isInQuietHours(
  utcTime: Date,
  quietHours: QuietHours,
  timezone: string
): boolean {
  return sharedIsInQuietHours(utcTime, quietHours, timezone);
}

/**
 * Adjust send time if it falls in quiet hours
 * Returns the next available time after quiet hours end
 */
export function adjustForQuietHours(
  sendAt: Date,
  quietHours: QuietHours,
  timezone: string
): Date {
  return sharedAdjustForQuietHours(sendAt, quietHours, timezone);
}

/**
 * Calculate reminder send times based on appointment time and cadence
 * Supports business day mode for smarter scheduling
 */
export function calculateReminderTimes(
  appointmentTime: Date | null,
  cadence: ReminderCadence,
  quietHours: QuietHours,
  timezone: string,
  now: Date = clock.now(),
  holidays: Holiday[] = []
): Array<{ type: 'immediate' | '24h' | '2h'; sendAt: Date }> {
  const reminders: Array<{ type: 'immediate' | '24h' | '2h'; sendAt: Date }> = [];
  
  // Immediate confirmation
  if (cadence.immediate) {
    const sendAt = adjustForQuietHours(now, quietHours, timezone);
    reminders.push({ type: 'immediate', sendAt });
  }
  
  // Only schedule time-based reminders if we have an appointment time
  if (!appointmentTime) {
    return reminders;
  }
  
  // 24 hours before (or 1 business day before if business day mode)
  if (cadence.hours24) {
    let reminderTime: Date;
    
    if (cadence.businessDayMode) {
      // 1 business day before appointment
      reminderTime = calculateBusinessDayReminderTime(
        appointmentTime,
        1, // 1 business day
        true,
        holidays,
        timezone
      );
    } else {
      // Standard 24 hours before
      reminderTime = addHours(appointmentTime, -24);
    }
    
    if (isAfter(reminderTime, now)) {
      const sendAt = adjustForQuietHours(reminderTime, quietHours, timezone);
      reminders.push({ type: '24h', sendAt });
    }
  }
  
  // 2 hours before
  if (cadence.hours2) {
    const reminderTime = addHours(appointmentTime, -2);
    if (isAfter(reminderTime, now)) {
      const sendAt = adjustForQuietHours(reminderTime, quietHours, timezone);
      reminders.push({ type: '2h', sendAt });
    }
  }
  
  return reminders;
}

/**
 * Format job summary for messages (truncate if needed)
 */
export function formatJobSummary(jobs: Array<{ name: string }>, maxLength: number = 100): string {
  if (jobs.length === 0) return 'Service appointment';
  if (jobs.length === 1) return jobs[0].name;
  
  const summary = jobs.map(j => j.name).join(' + ');
  
  if (summary.length <= maxLength) {
    return summary;
  }
  
  // Truncate and add ellipsis
  return summary.substring(0, maxLength - 3) + '...';
}

/**
 * Format vehicle info for messages
 */
export function formatVehicle(vehicle: {
  year: number;
  make: string;
  model: string;
}): string {
  return `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
}
